import React, { useEffect, useState, useRef } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, Card } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import { createDonationCardInstance } from '../../../Models/DonationCard';
import { availableAnimations } from '../../../Services/Animations/availableAnimations';

const DonationCardBuilder = () => {
    const [donationCardInstance, setDonationCardInstance] = useState(createDonationCardInstance());
    const [currentData, setCurrentData] = useState(donationCardInstance.getData());
    const [activeColorPicker, setActiveColorPicker] = useState(null);
    const colorSwatchRefs = useRef({});
    const [normalAnimation, setNormalAnimation] = useState(donationCardInstance.getNormalAnimation());
    const [progressingAnimation, setProgressingAnimation] = useState(donationCardInstance.getProgressingAnimation());

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        try {
            await donationCardInstance.load();
            setCurrentData(donationCardInstance.getData());
            setNormalAnimation(donationCardInstance.getNormalAnimation());
            setProgressingAnimation(donationCardInstance.getProgressingAnimation());
        } catch (error) {
            console.error(error);
            toast.error("Failed to load donation card data");
        }
    };

    const handleBorderRadiusChange = (cardType, elementType, value) => {
        const radius = value === 'rounded' ? '15px' : '0px';
        if (cardType === 'normal') {
            if (elementType === 'card') {
                donationCardInstance.setNormalCardBorderRadius(radius);
            } else if (elementType === 'image') {
                donationCardInstance.setNormalImageBorderRadius(radius);
            }
        } else {
            if (elementType === 'card') {
                donationCardInstance.setProgressingCardBorderRadius(radius);
            } else if (elementType === 'image') {
                donationCardInstance.setProgressingImageBorderRadius(radius);
            }
        }
        setCurrentData({ ...donationCardInstance.getData() });
    };

    const handleDisplayModeChange = (cardType, mode) => {
        if (cardType === 'normal') {
            donationCardInstance.setNormalCardDisplay(mode);
        } else {
            donationCardInstance.setProgressingCardDisplay(mode);
        }
        setCurrentData({ ...donationCardInstance.getData() });
    };

    const handleColorChange = (cardType, elementType, color) => {
        if (cardType === 'normal') {
            if (elementType === 'card') {
                donationCardInstance.setNormalCardBackgroundColor(color.hex);
                donationCardInstance.setNormalCardColor(color.hex);
            } else if (elementType === 'text') {
                donationCardInstance.setNormalTextColor(color.hex);
            } else if (elementType === 'textHover') {
                donationCardInstance.setNormalTextHoverColor(color.hex);
            }
        } else {
            if (elementType === 'card') {
                donationCardInstance.setProgressingCardBackgroundColor(color.hex);
                donationCardInstance.setProgressingCardColor(color.hex);
            } else if (elementType === 'text') {
                donationCardInstance.setProgressingTextColor(color.hex);
            } else if (elementType === 'textHover') {
                donationCardInstance.setProgressingTextHoverColor(color.hex);
            } else if (elementType === 'progressBar') {
                donationCardInstance.setProgressBarColor(color.hex);
            }
        }
        setCurrentData({ ...donationCardInstance.getData() });
    };

    const handleSaveChanges = async () => {
        try {
            donationCardInstance.setNormalAnimation(normalAnimation);
            donationCardInstance.setProgressingAnimation(progressingAnimation);
            await donationCardInstance.save();
            toast.success("Donation card changes saved successfully!");
            getData();
        } catch (error) {
            toast.error("Failed to save changes. Please try again.");
        }
    };

    const renderColorPicker = (show, color, onChange, onClose, swatchId) => {
        if (!show) return null;
        const swatchElement = colorSwatchRefs.current[swatchId];
        if (!swatchElement) return null;

        const pickerStyle = {
            position: 'absolute',
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '10px',
            zIndex: 1000
        };

        return (
            <>
                <div 
                    style={{ 
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 999
                    }}
                    onClick={onClose}
                />
                <div style={pickerStyle}>
                    <SketchPicker color={color} onChange={onChange} />
                </div>
            </>
        );
    };

    const renderColorSwatch = (color, onClick, swatchId) => (
        <div 
            ref={el => colorSwatchRefs.current[swatchId] = el}
            style={{
                width: '30px',
                height: '30px',
                backgroundColor: color,
                border: '1px solid #ccc',
                marginRight: '10px',
                cursor: 'pointer',
                borderRadius: '4px',
                position: 'relative'
            }}
            onClick={onClick}
        />
    );

    const renderBorderRadiusToggle = (cardType, elementType, currentValue) => {
        const isRounded = currentValue === '15px';
        return (
            <div className="d-flex gap-2">
                <Button
                    variant={!isRounded ? 'primary' : 'outline-primary'}
                    onClick={() => handleBorderRadiusChange(cardType, elementType, 'default')}
                    className="flex-grow-1"
                >
                    Default
                </Button>
                <Button
                    variant={isRounded ? 'primary' : 'outline-primary'}
                    onClick={() => handleBorderRadiusChange(cardType, elementType, 'rounded')}
                    className="flex-grow-1"
                >
                    Rounded
                </Button>
            </div>
        );
    };

    const renderDisplayModeToggle = (cardType, currentValue) => {
        const isNormal = currentValue === 'normal';
        return (
            <div className="d-flex gap-2">
                <Button
                    variant={isNormal ? 'primary' : 'outline-primary'}
                    onClick={() => handleDisplayModeChange(cardType, 'normal')}
                    className="flex-grow-1"
                >
                    Normal
                </Button>
                <Button
                    variant={!isNormal ? 'primary' : 'outline-primary'}
                    onClick={() => handleDisplayModeChange(cardType, 'overlayed')}
                    className="flex-grow-1"
                >
                    Overlayed
                </Button>
            </div>
        );
    };

    const renderColorControl = (label, color, cardType, elementType) => {
        const swatchId = `${cardType}-${elementType}`;
        return (
            <div className="d-flex align-items-center mb-3">
                <span className="me-2">{label}:</span>
                <div style={{ position: 'relative' }}>
                    {renderColorSwatch(color, () => 
                        setActiveColorPicker({
                            type: cardType,
                            elementType: elementType,
                            show: true,
                            swatchId: swatchId
                        }), swatchId
                    )}
                    {renderColorPicker(
                        activeColorPicker?.show && activeColorPicker?.swatchId === swatchId,
                        color,
                        (color) => handleColorChange(cardType, elementType, color),
                        () => setActiveColorPicker(null),
                        swatchId
                    )}
                </div>
                <span className="ms-2">{color}</span>
            </div>
        );
    };

    const renderCardSection = (cardType, data) => {
        const cardData = cardType === 'normal' ? data.NormalDonationCard : data.ProgressingDonationCard;
        const imageData = cardType === 'normal' ? data.NormalDonationCardImageContainer : data.ProgressingDonationCardImageContainer;
        const textData = cardType === 'normal' ? data.NormalDonationCardText : data.ProgressingDonationCardText;
        const textHoverData = cardType === 'normal' ? data.NormalDonationCardTextHover : data.ProgressingDonationCardTextHover;
        const displayMode = data.display;

        return (
            <div className="row">
                <div className="col-md-6">
                    <Card className="mb-4">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0">Card Styling</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4">
                                <h6>Display Mode</h6>
                                {renderDisplayModeToggle(cardType, displayMode)}
                            </div>
                            <div className="mb-4">
                                <h6>Card Border Radius</h6>
                                {renderBorderRadiusToggle(cardType, 'card', cardData.borderRadius)}
                            </div>
                            <div className="mb-4">
                                <h6>Image Border Radius</h6>
                                {renderBorderRadiusToggle(cardType, 'image', imageData.borderRadius)}
                            </div>
                            <div>
                                <h6>Card Colors</h6>
                                {renderColorControl('Background', cardData.backgroundColor, cardType, 'card')}
                            </div>
                        </Card.Body>
                    </Card>
                    <Card className="mb-4">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0">Animation</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <span className="me-2">Type:</span>
                                <select
                                    className="form-select"
                                    value={(cardType === 'normal' ? normalAnimation.animationType : progressingAnimation.animationType)}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (cardType === 'normal') {
                                            const updated = { ...normalAnimation, animationType: value };
                                            setNormalAnimation(updated);
                                        } else {
                                            const updated = { ...progressingAnimation, animationType: value };
                                            setProgressingAnimation(updated);
                                        }
                                    }}
                                >
                                    {availableAnimations.map((anim) => (
                                        <option key={anim} value={anim}>{anim}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="mb-3">
                                <span className="me-2">Duration (ms):</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    value={(cardType === 'normal' ? normalAnimation.animationDuration : progressingAnimation.animationDuration)}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (cardType === 'normal') {
                                            const updated = { ...normalAnimation, animationDuration: value };
                                            setNormalAnimation(updated);
                                        } else {
                                            const updated = { ...progressingAnimation, animationDuration: value };
                                            setProgressingAnimation(updated);
                                        }
                                    }}
                                />
                            </div>
                            <div className="mb-0">
                                <span className="me-2">Delay (ms):</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    value={(cardType === 'normal' ? normalAnimation.animationDelay : progressingAnimation.animationDelay)}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (cardType === 'normal') {
                                            const updated = { ...normalAnimation, animationDelay: value };
                                            setNormalAnimation(updated);
                                        } else {
                                            const updated = { ...progressingAnimation, animationDelay: value };
                                            setProgressingAnimation(updated);
                                        }
                                    }}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-md-6">
                    <Card className="mb-4">
                        <Card.Header className="bg-light">
                            <h5 className="mb-0">Text Styling</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4">
                                <h6>Text Colors</h6>
                                {renderColorControl('Normal', textData.color, cardType, 'text')}
                                {renderColorControl('Hover', textHoverData.color, cardType, 'textHover')}
                            </div>
                        </Card.Body>
                    </Card>

                    {cardType === 'progressing' && (
                        <Card>
                            <Card.Header className="bg-light">
                                <h5 className="mb-0">Progress Bar</h5>
                            </Card.Header>
                            <Card.Body>
                                {renderColorControl('Color', data.ProgressingDonationCardProgressBar.backgroundColor, cardType, 'progressBar')}
                            </Card.Body>
                        </Card>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Donation Card Builder</h3>
            </div>

            <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Normal Donation Card</Accordion.Header>
                    <Accordion.Body>
                        {renderCardSection('normal', currentData.normalCard)}
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Progressing Donation Card</Accordion.Header>
                    <Accordion.Body>
                        {renderCardSection('progressing', currentData.progressingCard)}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            
            <div className="SaveChangesContainer d-flex justify-content-end mt-2">
                <ToastContainer />
                <Button variant='primary' onClick={handleSaveChanges}>Save Changes</Button>
            </div>
        </div>
    );
};

export default DonationCardBuilder; 